import {
  LEGISLATOR_MANAGEMENT_MODULE,
  REGISTRATION_MODULE,
  STANDARD_DICTIONARIES_MODULE,
} from 'config/api-variables';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { getUserType, logoutClearLocal } from 'utils/local-storage/user';
import {
  handleErrorMessage,
  sagaPaginationRequestTemplate,
} from 'utils/tools/slice-actions';
import {
  selectConstituenciesLastFetch,
  selectConstituenciesList,
  selectCountriesCodeLastFetch,
  selectCountriesCodeList,
  selectCurrencyLastFetch,
  selectCurrencyList,
  selectDateFormatsLastFetch,
  selectDateFormatsList,
  selectDictionaryLastFetch,
  selectDictionaryList,
  selectIssueSourcesLastFetch,
  selectIssueSourcesList,
  selectLanguagesLastFetch,
  selectLanguagesList,
  selectMasterSlice,
  selectRolesLastFetch,
  selectRolesList,
  selectStatesLastFetch,
  selectStatesList,
} from './selectors';

import { StatusCodes } from 'config/global-enums';
import { masterDataActions as actions } from '.';
import { request } from 'utils/request';

function* masterFetchProcess(
  selectPreviousData,
  selectPreviousDataFetchTime,
  loadEndAction,
  requestURL,
  reqPayload,
  loadFor,
  needsAuth,
) {
  const previousData = yield select(selectPreviousData);
  const previousDataFetchTime = yield select(selectPreviousDataFetchTime);

  const dayDifference = Math.floor(
    (Date.now() - previousDataFetchTime) / 1000 / 60 / 60 / 24,
  );

  if (previousData && dayDifference < 1) {
    yield put(
      actions[loadEndAction]({
        data: previousData,
        time: previousDataFetchTime,
        ...reqPayload,
      }),
    );
    return;
  }

  try {
    const response = yield call(request, requestURL, reqPayload, !!needsAuth);
    if (response?.statusCode === StatusCodes.SUCCESS_CODE) {
      yield put(
        actions[loadEndAction]({
          data: response?.rows,
          time: Date.now(),
          ...reqPayload,
        }),
      );
    } else {
      const errMsg = response?.message;
      yield call(handleErrorMessage, errMsg, actions, 'masterLoadError');
    }
  } catch (err) {
    if (err?.response?.status === 404) {
      const errMsg = `Problem loading ${loadFor}!`;
      yield call(handleErrorMessage, errMsg, actions, 'masterLoadError');
    }
  }
}

export function* fetchCountriesCodeList() {
  yield call(
    masterFetchProcess,
    selectCountriesCodeList,
    selectCountriesCodeLastFetch,
    'countriesCodeLoadEnd',
    REGISTRATION_MODULE + '/countrymobile/list/',
    { app_type: getUserType() },
    'Country Codes',
  );
}

export function* fetchRolesList({ payload }) {
  yield call(
    masterFetchProcess,
    selectRolesList(payload?.level_type),
    selectRolesLastFetch(payload?.level_type),
    'rolesLoadEnd',
    STANDARD_DICTIONARIES_MODULE + '/persontype/list',
    // { app_type: getUserType() },
    { ...payload },
    'Roles',
    true,
  );
}

export function* fetchStatesList() {
  yield call(
    masterFetchProcess,
    selectStatesList,
    selectStatesLastFetch,
    'statesLoadEnd',
    REGISTRATION_MODULE + '/states/',
    { country: 'IN', app_type: getUserType() },
    'States',
  );
}

export function* fetchConstituencyList({ payload }) {
  yield call(
    masterFetchProcess,
    selectConstituenciesList,
    selectConstituenciesLastFetch,
    'constituenciesLoadEnd',
    STANDARD_DICTIONARIES_MODULE + '/constituency/list',
    { app_type: getUserType(), ...payload },
    'Constituencies',
    true,
  );
}

export function* fetchLanguagesList({ payload }) {
  yield call(
    masterFetchProcess,
    selectLanguagesList,
    selectLanguagesLastFetch,
    'languagesLoadEnd',
    STANDARD_DICTIONARIES_MODULE + '/applanguage/list',
    { app_type: getUserType(), ...payload },
    'Languages',
    true,
  );
}

export function* fetchDateFormatsList({ payload }) {
  yield call(
    masterFetchProcess,
    selectDateFormatsList,
    selectDateFormatsLastFetch,
    'dateFormatsLoadEnd',
    STANDARD_DICTIONARIES_MODULE + '/dateformat/list',
    { app_type: getUserType(), ...payload },
    'Date Formats',
    true,
  );
}

export function* fetchCurrencyList({ payload }) {
  yield call(
    masterFetchProcess,
    selectCurrencyList,
    selectCurrencyLastFetch,
    'currencyLoadEnd',
    STANDARD_DICTIONARIES_MODULE + '/currency/list',
    { app_type: getUserType(), ...payload },
    'Currency',
    // true,
    false,
  );
}

export function* fetchDictionaryData({ payload }) {
  yield call(
    masterFetchProcess,
    selectDictionaryList(payload?.type),
    selectDictionaryLastFetch(payload?.type),
    'dictionaryLoadEnd',
    STANDARD_DICTIONARIES_MODULE + '/dictionary/list',
    { app_type: getUserType(), ...payload },
    'details...',
    true,
  );
}

export function* fetchSecondaryDictionaryData({ payload }) {
  yield call(
    masterFetchProcess,
    selectDictionaryList(payload?.type),
    selectDictionaryLastFetch(payload?.type),
    'dictionaryLoadEnd',
    STANDARD_DICTIONARIES_MODULE + '/dictionary/list2',
    { app_type: getUserType(), ...payload },
    'details...',
    false,
  );
}

function* fetchLocalitiesList({ payload }) {
  yield call(
    sagaPaginationRequestTemplate,
    STANDARD_DICTIONARIES_MODULE + '/constituency/localities/list',
    payload,
    actions,
    'fetchPageActionEnd',
    'fetchPageActionError',
    'Unable to load localities!',
    selectMasterSlice,
  );
}

export function* fetchKeywordList({ payload }) {
  yield call(
    sagaPaginationRequestTemplate,
    STANDARD_DICTIONARIES_MODULE + '/keyword/list',
    payload,
    actions,
    'fetchPageActionEnd',
    'fetchPageActionError',
    'Unable to load keywords!',
    selectMasterSlice,
  );
}

export function* generateSubdomain({ payload }) {
  const requestURL = LEGISLATOR_MANAGEMENT_MODULE + '/domain/generate';

  try {
    const response = yield call(request, requestURL, payload);
    if (response.statusCode === StatusCodes.SUCCESS_CODE) {
      yield put(actions.generateSubdomainEnd(response.rows.subdomain));
    } else {
      yield call(
        handleErrorMessage,
        response?.message,
        actions,
        'generateSubdomainError',
      );
      if (response?.status === 401) setTimeout(logoutClearLocal, 2300);
    }
  } catch (err) {
    yield call(
      handleErrorMessage,
      'Unable to generate subdomain!',
      actions,
      'generateSubdomainError',
    );
  }
}

export function* verifySubomain({ payload }) {
  const requestURL = LEGISLATOR_MANAGEMENT_MODULE + '/domain/verify';

  try {
    const response = yield call(request, requestURL, payload);
    if (response.statusCode === StatusCodes.SUCCESS_CODE) {
      yield put(actions.verifySubdomainEnd(response.rows.is_unique));
    } else {
      yield call(
        handleErrorMessage,
        response?.message,
        actions,
        'verifySubdomainError',
      );
      if (response?.status === 401) setTimeout(logoutClearLocal, 2300);
    }
  } catch (err) {
    yield call(
      handleErrorMessage,
      'Unable to verify subdomain!',
      actions,
      'verifySubdomainError',
    );
  }
}

export function* fetchIssueSourceList({ payload }) {
  yield call(
    masterFetchProcess,
    selectIssueSourcesList,
    selectIssueSourcesLastFetch,
    'issueSourceListLoadEnd',
    STANDARD_DICTIONARIES_MODULE + '/issuesource/list',
    { app_type: getUserType(), ...payload },
    'Issue Source',
    true,
    // false,
  );
}

export function* masterDataSaga() {
  yield takeLatest(actions.countriesCodeLoadStart.type, fetchCountriesCodeList);
  yield takeEvery(actions.rolesLoadStart.type, fetchRolesList);
  yield takeLatest(actions.statesLoadStart.type, fetchStatesList);
  yield takeLatest(actions.constituenciesLoadStart.type, fetchConstituencyList);
  yield takeLatest(actions.languagesLoadStart.type, fetchLanguagesList);
  yield takeLatest(actions.dateFormatsLoadStart.type, fetchDateFormatsList);
  yield takeLatest(actions.currencyLoadStart.type, fetchCurrencyList);
  yield takeLatest(actions.dictionaryLoadStart.type, fetchDictionaryData);
  yield takeLatest(
    actions.secondaryDictionaryLoadStart.type,
    fetchSecondaryDictionaryData,
  );
  yield takeLatest(actions.fetchLocalitiesListStart.type, fetchLocalitiesList);

  yield takeLatest(actions.generateSubdomainStart.type, generateSubdomain);
  yield takeLatest(actions.verifySubdomainStart.type, verifySubomain);

  yield takeLatest(actions.issueSourceListLoadStart.type, fetchIssueSourceList);
}
